/**
 * widgetHelpers.js – Utility functions for managing visualization widget nodes.
 *
 * Handles the creation, restoration, and position management of floating
 * widget nodes and their connector edges on the SCADA canvas.
 */

/**
 * Default widget offset from parent node (placed below-right).
 */
const DEFAULT_WIDGET_OFFSET = { x: 30, y: 140 };

/**
 * Generate widget node ID from parent node ID.
 */
export const getWidgetNodeId = (parentNodeId) => `widget-${parentNodeId}`;

/**
 * Generate widget connector edge ID.
 */
export const getWidgetEdgeId = (parentNodeId) => `widget-edge-${parentNodeId}`;

/**
 * Check if a node should have a visualization widget.
 * Returns true if the node has a device binding AND visualization config.
 */
export const shouldHaveWidget = (node) => {
  const data = node.data || {};
  // Check both root and nested data for config
  const config = data.config || node.config || {};
  const visualization = config.visualization || node.visualization || "value";
  const deviceId = data.deviceId || node.deviceId;
  const parameterId = data.parameterId || node.parameterId;
  const widgetConfig = data.visualizationWidget || node.visualizationWidget || {};

  const hasDevice = !!deviceId && !!parameterId;

  // Widget is enabled if explicitly enabled, or if there's a device binding
  // and visualization type is configured
  return hasDevice && (widgetConfig.enabled !== false) && visualization !== "level" && visualization !== "none";
};

/**
 * Create a widget node from a parent SCADA node.
 */
export const createWidgetNode = (parentNode) => {
  const data = parentNode.data || {};
  const config = data.config || parentNode.config || {};
  const widgetConfig = data.visualizationWidget || parentNode.visualizationWidget || {};
  const parentPos = parentNode.position || { x: 0, y: 0 };

  // Use saved widget position, or calculate from offset
  const widgetPos = widgetConfig.position || config.widgetPosition || {
    x: parentPos.x + DEFAULT_WIDGET_OFFSET.x,
    y: parentPos.y + DEFAULT_WIDGET_OFFSET.y,
  };

  // Use saved widget dimensions (standard compact SCADA defaults)
  const visualization = config.visualization || parentNode.visualization || "value";
  const defaultWidth = visualization === "graph" ? 180 : (visualization === "gauge" ? 130 : 100);
  const defaultHeight = visualization === "graph" ? 110 : (visualization === "gauge" ? 130 : 40);

  const widgetWidth = widgetConfig.width || config.widgetWidth || defaultWidth;
  const widgetHeight = widgetConfig.height || config.widgetHeight || defaultHeight;

  return {
    id: getWidgetNodeId(parentNode.id),
    type: "visualizationWidget",
    position: widgetPos,
    width: widgetWidth,
    height: widgetHeight,
    data: {
      parentNodeId: parentNode.id,
      widgetType: config.visualization || parentNode.visualization || "value",
      deviceId: data.deviceId || parentNode.deviceId,
      parameterId: data.parameterId || parentNode.parameterId,
      title: data.title || parentNode.title || "",
      config: {
        minValue: config.minValue ?? parentNode.minValue ?? 0,
        maxValue: config.maxValue ?? parentNode.maxValue ?? 100,
        unit: config.unit || parentNode.unit || "",
        fontSize: config.fontSize ?? parentNode.fontSize ?? 14,
        color: (data.styles || parentNode.styles)?.color || "#3b82f6",
        warningThreshold: config.warningThreshold || parentNode.warningThreshold,
        criticalThreshold: config.criticalThreshold || parentNode.criticalThreshold,
        // Dial color zone thresholds — must be forwarded so GaugeWidget can render
        // the correct Green/Yellow/Red arc segments
        greenStart: config.greenStart ?? parentNode.greenStart ?? null,
        greenEnd: config.greenEnd ?? parentNode.greenEnd ?? null,
        yellowStart: config.yellowStart ?? parentNode.yellowStart ?? null,
        yellowEnd: config.yellowEnd ?? parentNode.yellowEnd ?? null,
        redStart: config.redStart ?? parentNode.redStart ?? null,
        redEnd: config.redEnd ?? parentNode.redEnd ?? null,
        widgetWidth: widgetWidth,
        widgetHeight: widgetHeight,
      },
    },
    // Widget nodes should not show in the save payload as standalone components
    draggable: true,
    selectable: true,
    deletable: false, // Prevent accidental deletion (managed by parent)
  };
};

/**
 * Create a connector edge between parent node and widget node.
 */
export const createWidgetEdge = (parentNodeId) => ({
  id: getWidgetEdgeId(parentNodeId),
  source: parentNodeId,
  target: getWidgetNodeId(parentNodeId),
  type: "widgetConnector",
  // Mark this as a widget edge so it's excluded from save/delete flows
  data: { isWidgetEdge: true },
  selectable: false,
  deletable: false,
  focusable: false,
  updatable: false,
});

/**
 * Generate all widget nodes and edges from the canvas nodes.
 * Called during canvas initialization to restore widget positions.
 *
 * @param {Array} nodes - The SCADA component nodes from the canvas
 * @returns {{ widgetNodes: Array, widgetEdges: Array }}
 */
export const generateWidgetsFromNodes = (nodes) => {
  const widgetNodes = [];
  const widgetEdges = [];

  for (const node of nodes) {
    // Skip widget nodes and junction nodes
    if (node.type === "visualizationWidget" || node.type === "junction") continue;

    if (shouldHaveWidget(node)) {
      widgetNodes.push(createWidgetNode(node));
      widgetEdges.push(createWidgetEdge(node.id));
    }
  }

  return { widgetNodes, widgetEdges };
};

/**
 * Extract widget states (position & size) from current nodes to save back into parent node data.
 * Called during save to persist widget states.
 *
 * @param {Array} allNodes - All nodes on the canvas (including widget nodes)
 * @returns {Object} Map of parentNodeId -> { x, y, width, height } widget state
 */
export const extractWidgetStates = (allNodes) => {
  const states = {};

  for (const node of allNodes) {
    if (node.type === "visualizationWidget" && node.data?.parentNodeId) {
      states[node.data.parentNodeId] = {
        position: {
          x: node.position.x,
          y: node.position.y,
        },
        width: node.width || node.measured?.width,
        height: node.height || node.measured?.height,
      };
    }
  }

  return states;
};

/**
 * Check if a node is a widget node (not a real SCADA component).
 */
export const isWidgetNode = (node) => node.type === "visualizationWidget";

/**
 * Check if an edge is a widget connector (not a real pipe connection).
 */
export const isWidgetEdge = (edge) =>
  edge.type === "widgetConnector" || edge.data?.isWidgetEdge === true;

/**
 * Filter out widget nodes/edges from arrays for persistence.
 */
export const filterOutWidgets = (nodes, edges) => ({
  nodes: nodes.filter((n) => !isWidgetNode(n)),
  edges: edges.filter((e) => !isWidgetEdge(e)),
});
